import PropTypes from "prop-types";
import React, { Fragment, useCallback, useMemo, useState } from "react";

import { useTheme } from "@mui/material/styles";
import {
  CardContent,
  Grid,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import useConfig from "hooks/useConfig";
import MainCard from "components/common/card/MainCard";
import { gridSpacing } from "constants/theme";
import PersonOutlineTwoToneIcon from "@mui/icons-material/PersonOutlineTwoTone";
import VpnKeyTwoToneIcon from "@mui/icons-material/VpnKeyTwoTone";
import { useSelector } from "react-redux";
import { Loading } from "components/common";
import UpdateUserProfileContainer from "containers/profile/update/UpdateUserProfile.container";
import ProfileChangePasswordContainer from "containers/profile/changePassword/ProfileChangePassword.container";
import { useTranslation } from "react-i18next";

// tabs
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <div>{children}</div>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}


const SettingsProfileContainer = () => {
  const { t } = useTranslation()
  const theme = useTheme();
  const { borderRadius } = useConfig();
  const [value, setValue] = useState(0);
  const { loading, user } = useSelector((state) => state.user);

  // tabs option
  const tabsOption = useMemo(() => (
    [
      {
        label: t('User profile'),
        icon: <PersonOutlineTwoToneIcon />,
        caption: t('Profile Settings'),
      },
      {
        label: t('Change Password'),
        icon: <VpnKeyTwoToneIcon />,
        caption: t('Security'),
      },
    ]
  ), [t])


  const handleChange = useCallback((event, newValue) => {
    setValue(newValue);
  }, []);

  return (
    <Fragment>
      {loading && <Loading />}
      <Grid container  >
        <Grid item xs={12}>
          <MainCard title={t('Account settings')} content={false}>
            <Grid container spacing={gridSpacing}>
              <Grid item xs={12} sm={4} md={4} lg={4}>
                <CardContent>
                  <Tabs
                    value={value}
                    onChange={handleChange}
                    orientation="vertical"
                    variant="scrollable"
                    sx={{
                      "& .MuiTabs-flexContainer": {
                        borderBottom: "none",
                      },
                      "& button": {
                        color:
                          theme.palette.mode === "dark"
                            ? theme.palette.grey[600]
                            : theme.palette.grey[600],
                        minHeight: "auto",
                        minWidth: "100%",
                        py: 1.5,
                        px: 2,
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "flex-start",
                        textAlign: "left",
                        justifyContent: "flex-start",
                        borderRadius: `${borderRadius}px`,
                      },
                      "& button.Mui-selected": {
                        color: theme.palette.primary.main,
                        background:
                          theme.palette.mode === "dark"
                            ? theme.palette.dark.main
                            : theme.palette.grey[50],
                      },
                      "& button > svg": {
                        marginBottom: "0px !important",
                        marginRight: 1.25,
                        marginTop: 1.25,
                        height: 20,
                        width: 20,
                      },
                      "& button > div > span": {
                        display: "block",
                      },
                      "& > div > span": {
                        display: "none",
                      },
                    }}
                  >
                    {tabsOption.map((tab, index) => (
                      <Tab
                        key={index}
                        icon={tab.icon}
                        label={
                          <Grid container direction="column">
                            <Typography variant="subtitle1" color="inherit">
                              {tab.label}
                            </Typography>
                            <Typography
                              component="div"
                              variant="caption"
                              sx={{ textTransform: "capitalize" }}
                            >
                              {tab.caption}
                            </Typography>
                          </Grid>
                        }
                        {...a11yProps(index)}
                      />
                    ))}
                  </Tabs>
                </CardContent>
              </Grid>
              <Grid item xs={12} sm={8} md={8} lg={8}>
                <CardContent
                  sx={{
                    borderLeft: "1px solid",
                    borderColor:
                      theme.palette.mode === "dark"
                        ? theme.palette.background.default
                        : theme.palette.grey[200],
                    height: "100%",
                  }}
                >
                  <TabPanel value={value} index={0}>
                    <UpdateUserProfileContainer id={user?.userId} />
                  </TabPanel>

                  <TabPanel value={value} index={1}>
                    <ProfileChangePasswordContainer />
                  </TabPanel>
                </CardContent>
              </Grid>
            </Grid>

          </MainCard>
        </Grid>
      </Grid>
    </Fragment>
  );
};

export default SettingsProfileContainer;
